import { c as _c } from "react/compiler-runtime";
import { type BannerVariant, type VariantConfig, type BannerProps } from './types';
import { AlertTipIcon } from '../icons/AlertTipIcon';
import { AlertInfoIcon } from '../icons/AlertInfoIcon';
import { AlertErrorIcon } from '../icons/AlertErrorIcon';
import { AlertWarningIcon } from '../icons/AlertWarningIcon';
import { LinkIcon } from '../icons/LinkIcon';
import { CancelBkgIcon } from '../icons/CancelBkgIcon';
import { useBannerSize, useBannerLayout } from './hooks';
import { useTheme } from '../../utils/useTheme';
import { Box } from '@mui/material';
import Divider from '../Divider';
import { StyledLabelContent } from './styles';
import Button from '../Button';
import Label from '../Label';
import Typography from '../Typography';
const VARIANT_CONFIG: Record<BannerVariant, VariantConfig> = {
  tip: {
    label: 'Tip',
    color: 'success',
    Icon: AlertTipIcon
  },
  info: {
    label: 'Info',
    color: 'info',
    Icon: AlertInfoIcon
  },
  warning: {
    label: 'Warning',
    color: 'warning',
    Icon: AlertWarningIcon
  },
  error: {
    label: 'Error',
    color: 'error',
    Icon: AlertErrorIcon
  }
};
const Banner = props => {
  const $ = _c(54);
  const theme = useTheme();
  const {
    ref,
    isLarge,
    isSmall
  } = useBannerSize();
  const {
    label,
    color,
    Icon
  } = VARIANT_CONFIG[props.variant];
  const layout = useBannerLayout(isSmall, isLarge);
  const borderColor = theme.tokens.border.main.tonal;
  const backgroundColor = theme.tokens.background.main.default;
  const t0 = `1px solid ${borderColor}`;
  let t1;
  if ($[0] !== backgroundColor || $[1] !== layout.masterDirection || $[2] !== t0) {
    t1 = {
      border: t0,
      borderRadius: "32px",
      backgroundColor,
      width: "100%",
      position: "relative",
      overflow: "hidden",
      p: "20px",
      display: "flex",
      flexDirection: layout.masterDirection,
      alignItems: "stretch",
      gap: "24px"
    };
    $[0] = backgroundColor;
    $[1] = layout.masterDirection;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== isSmall || $[5] !== props.onClose) {
    t2 = props.onClose != null && isSmall && <Box sx={{
      position: "absolute",
      top: 16,
      right: 16,
      zIndex: 2
    }}><Button rightIcon={<CancelBkgIcon />} onClick={props.onClose} scheme="light" variant="text" /></Box>;
    $[4] = isSmall;
    $[5] = props.onClose;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = isLarge ? "center" : "flex-start";
  const t4 = isLarge ? "24px" : "16px";
  let t5;
  if ($[7] !== layout.contentDirection || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      flexGrow: 1,
      display: "flex",
      flexDirection: layout.contentDirection,
      alignItems: t3,
      gap: t4
    };
    $[7] = layout.contentDirection;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = isSmall ? "8px" : "24px";
  const t7 = isLarge ? "auto" : "100%";
  let t8;
  if ($[11] !== layout.textGroupAlign || $[12] !== layout.textGroupDirection || $[13] !== t6 || $[14] !== t7) {
    t8 = {
      display: "flex",
      flexDirection: layout.textGroupDirection,
      gap: t6,
      alignItems: layout.textGroupAlign,
      flexGrow: 1,
      width: t7
    };
    $[11] = layout.textGroupAlign;
    $[12] = layout.textGroupDirection;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      flexShrink: 0
    };
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== Icon) {
    t10 = <div><Icon /></div>;
    $[17] = Icon;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== label || $[20] !== t10) {
    t11 = <StyledLabelContent>{t10}{label}</StyledLabelContent>;
    $[19] = label;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== color || $[23] !== t11) {
    t12 = <Box sx={t9}><Label color={color} text={t11} /></Box>;
    $[22] = color;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      flexGrow: 1,
      textAlign: "left"
    };
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== props.annotation) {
    t14 = props.annotation != null && props.annotation !== "" && <strong>{props.annotation} </strong>;
    $[26] = props.annotation;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== props.content || $[29] !== t14) {
    t15 = <Box sx={t13}><Typography variant="body1">{t14}{props.content}</Typography></Box>;
    $[28] = props.content;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t12 || $[32] !== t15 || $[33] !== t8) {
    t16 = <Box sx={t8}>{t12}{t15}</Box>;
    $[31] = t12;
    $[32] = t15;
    $[33] = t8;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== isLarge || $[36] !== layout.buttonDirection || $[37] !== layout.buttonsFullWidth || $[38] !== props.dismissButton || $[39] !== props.learnMoreButton) {
    t17 = (props.learnMoreButton != null || props.dismissButton != null) && <Box sx={{
      display: "flex",
      flexDirection: layout.buttonDirection,
      gap: "8px",
      width: layout.buttonsFullWidth ? "100%" : "auto",
      flexShrink: 0,
      alignSelf: isLarge ? "auto" : "flex-start"
    }}>{props.learnMoreButton != null && <Button rightIcon={<LinkIcon />} variant="tonal" style={{
        width: layout.buttonsFullWidth ? "100%" : "auto"
      }} {...props.learnMoreButton} />}{props.dismissButton != null && <Button variant="text" style={{
        width: layout.buttonsFullWidth ? "100%" : "auto"
      }} {...props.dismissButton} />}</Box>;
    $[35] = isLarge;
    $[36] = layout.buttonDirection;
    $[37] = layout.buttonsFullWidth;
    $[38] = props.dismissButton;
    $[39] = props.learnMoreButton;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t16 || $[42] !== t17 || $[43] !== t5) {
    t18 = <Box sx={t5}>{t16}{t17}</Box>;
    $[41] = t16;
    $[42] = t17;
    $[43] = t5;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== isSmall || $[46] !== props.onClose) {
    t19 = props.onClose != null && !isSmall && <Box sx={{
      display: "flex",
      alignItems: "stretch",
      justifyContent: "center",
      gap: "16px"
    }}><Divider vertical={true} /><Button rightIcon={<CancelBkgIcon />} variant="text" onClick={props.onClose} style={{
        alignSelf: "center",
        height: "40px",
        width: "40px"
      }} /></Box>;
    $[45] = isSmall;
    $[46] = props.onClose;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== ref || $[49] !== t1 || $[50] !== t18 || $[51] !== t19 || $[52] !== t2) {
    t20 = <Box ref={ref} sx={t1}>{t2}{t18}{t19}</Box>;
    $[48] = ref;
    $[49] = t1;
    $[50] = t18;
    $[51] = t19;
    $[52] = t2;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  return t20;
};
export default Banner;
export { Banner };
export type { BannerProps, BannerButton } from './types';